import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CancelIcon from '@material-ui/icons/Cancel';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  warningIcon: {
    fontSize: 60,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  warningHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  cancelButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const TripCancelPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchTrip();
  }, [id]);

  const fetchTrip = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const tripDoc = doc(firestore, 'trips', id);
      const tripSnapshot = await getDoc(tripDoc);
      
      if (tripSnapshot.exists()) {
        setTrip({
          id: tripSnapshot.id,
          ...tripSnapshot.data()
        });
      } else {
        // Si le voyage n'existe pas, utiliser des données de démonstration
        setTrip({
          id: id,
          routeId: '1',
          routeName: 'Paris - Lyon',
          departureDate: '2025-04-15T08:00:00',
          arrivalDate: '2025-04-15T12:30:00',
          status: 'Scheduled',
          availableSeats: 45,
          bookedSeats: 32,
          price: 45.99,
          vehicleId: 'V001',
          vehicleName: 'Bus 101',
          driverId: 'D001',
          driverName: 'Jean Dupont',
          notes: 'Voyage régulier entre Paris et Lyon. Arrêt de 15 minutes à mi-parcours.',
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error fetching trip:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données du voyage',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
  };

  const handleCancel = () => {
    navigate(`/admin/trips/${id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      setSnackbar({
        open: true,
        message: 'Veuillez fournir une raison d\'annulation',
        severity: 'error'
      });
      return;
    }
    
    setOpenConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    setOpenConfirmDialog(false);
    setSubmitting(true);
    
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const tripDoc = doc(firestore, 'trips', id);
      await updateDoc(tripDoc, {
        status: 'Cancelled',
        cancelledAt: new Date(),
        cancellationReason: reason
      });
      
      setSnackbar({
        open: true,
        message: 'Voyage annulé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la page de détails après un court délai
      setTimeout(() => {
        navigate(`/admin/trips/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error cancelling trip:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'annulation du voyage',
        severity: 'error'
      });
      setSubmitting(false);
    }
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour aux détails
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Annuler le voyage
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : trip ? (
          <>
            <div className={classes.warningHeader}>
              <CancelIcon className={classes.warningIcon} />
              <Typography variant="h5" component="h2" gutterBottom>
                Vous êtes sur le point d'annuler le voyage suivant :
              </Typography>
            </div>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">
                  <strong>Itinéraire :</strong> {trip.routeName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">
                  <strong>Véhicule :</strong> {trip.vehicleName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">
                  <strong>Date de départ :</strong> {formatDate(trip.departureDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">
                  <strong>Date d'arrivée :</strong> {formatDate(trip.arrivalDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">
                  <strong>Chauffeur :</strong> {trip.driverName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">
                  <strong>Réservations :</strong> {trip.bookedSeats} sièges réservés
                </Typography>
              </Grid>
            </Grid>
            
            <Box mt={4} mb={2}>
              <Typography variant="h6" color="error" gutterBottom>
                Attention : Cette action est irréversible !
              </Typography>
              <Typography variant="body1" paragraph>
                L'annulation de ce voyage entraînera l'envoi automatique d'une notification à tous les passagers ayant effectué une réservation. 
                Les réservations associées seront marquées comme annulées et les remboursements seront traités conformément à la politique de remboursement.
              </Typography>
            </Box>
            
            <form className={classes.form} onSubmit={handleSubmit}>
              <TextField
                name="reason"
                label="Raison de l'annulation"
                variant="outlined"
                fullWidth
                required
                multiline
                rows={4}
                value={reason}
                onChange={handleReasonChange}
                placeholder="Veuillez indiquer la raison de l'annulation de ce voyage..."
              />
              
              <div className={classes.buttons}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCancel}
                  className={classes.button}
                >
                  Retour
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className={`${classes.button} ${classes.cancelButton}`}
                  disabled={submitting}
                  startIcon={<CancelIcon />}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Confirmer l\'annulation'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <Typography variant="body1">
            Voyage non trouvé
          </Typography>
        )}
      </Paper>
      
      {/* Dialog de confirmation d'annulation */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer l'annulation du voyage"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous absolument sûr de vouloir annuler ce voyage ? Cette action est irréversible et entraînera l'envoi de notifications à tous les passagers concernés.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Retour
          </Button>
          <Button onClick={handleConfirmCancel} color="secondary" autoFocus>
            Confirmer l'annulation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TripCancelPage;
